import { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';
import { readContract, writeContract } from '@wagmi/core';
import { type TransactionExecutionError, UserRejectedRequestError } from 'viem';

import { useWatchAssets } from '@pkg/hooks/nft/useWatchAssets';
import { useAppDispatch } from '@pkg/hooks/redux';
import { wagmiConfig } from '@pkg/providers';

import { type ArtifactSignatureParams, ArtifactType, Events } from '@app/client';
import { callGenerateSignature } from '@app/client/resources/nft/artifacts';
import { callSetName } from '@app/client/resources/nft/nft';
import config from '@app/config';
import { getOpenSeaMintedYatUrl, prefetch2FA, yatAbi } from '@app/helpers';
import { registerEvent } from '@app/store/actions/events';
import { setLootBoxTokenIdMapping } from '@app/store/actions/minting';
import { setOpenSeaUrl } from '@app/store/actions/nft';
import { type LootBoxId } from '@app/store/types/artifacts';
import { useMintingOverlayStore } from '@app/store/zustand';

const { tokenContractAddress } = config;
interface GenerateSignatureArgs {
    value: ArtifactSignatureParams;
    type: ArtifactType;
    id: string;
}
interface MintTokenArgs {
    account: string;
    token: string;
    type?: ArtifactType;
    yatName?: string;
}

export function useGenerateSignature() {
    const dispatch = useAppDispatch();
    const { setMintingInitiated } = useMintingOverlayStore();

    return useMutation({
        mutationFn: (args: GenerateSignatureArgs) =>
            callGenerateSignature({ ...args, prefetch: prefetch2FA(dispatch, false, () => setMintingInitiated(true)) }),
        onError: () => false,
    });
}

export function useSetYatName() {
    return useMutation({
        mutationFn: ({ name, eid }: { name: string; eid: string }) => callSetName({ value: { data: { name } }, eid }),
    });
}

export function useMint(
    { contractAddress, onRejectTransaction }: { contractAddress?: string; onRejectTransaction?: () => void } = {
        contractAddress: tokenContractAddress,
    },
) {
    const dispatch = useAppDispatch();
    const { setMintingFailed, setMintingInitiated, setMintingSuccessOptimistic } = useMintingOverlayStore();
    const { mutateAsync: generateSignature } = useGenerateSignature();

    const watchAssets = useWatchAssets();
    const mintContractAddress = contractAddress || tokenContractAddress;

    const handleMint = useCallback(
        async (type: ArtifactType, from: string, token: string) => {
            try {
                const signatureRes = await generateSignature({ value: { account: from }, type, id: token });
                if (signatureRes?.ok) {
                    const { expiry, signature } = signatureRes.value;
                    try {
                        const transactionHash = await writeContract(wagmiConfig, {
                            address: mintContractAddress as `0x${string}`,
                            abi: yatAbi,
                            functionName: 'mint',
                            maxFeePerGas: undefined,
                            maxPriorityFeePerGas: undefined,
                            args: [token, from as `0x${string}`, BigInt(expiry), signature as `0x${string}`],
                        });

                        if (transactionHash) {
                            if (type === ArtifactType.Yat) {
                                registerEvent(Events.yat_minted, { yat_unicode: token });
                            }

                            const userBalance = await readContract(wagmiConfig, {
                                address: mintContractAddress as `0x${string}`,
                                abi: yatAbi,
                                functionName: 'balanceOf',
                                args: [from as `0x${string}`],
                            });

                            if (userBalance > 0) {
                                const lastTokenId = await readContract(wagmiConfig, {
                                    address: mintContractAddress as `0x${string}`,
                                    abi: yatAbi,
                                    functionName: 'tokenOfOwnerByIndex',
                                    args: [from as `0x${string}`, BigInt(userBalance) - BigInt(1)],
                                });

                                if (lastTokenId) {
                                    setMintingSuccessOptimistic(true);
                                    if (type !== ArtifactType.Yat) {
                                        registerEvent(Events.artifact_minted);
                                        dispatch(setLootBoxTokenIdMapping({ [token as LootBoxId]: lastTokenId }));
                                    }
                                    const url = getOpenSeaMintedYatUrl(lastTokenId.toString());
                                    dispatch(setOpenSeaUrl(url));
                                    const added = await watchAssets({
                                        tokenId: lastTokenId.toString(),
                                        address: mintContractAddress,
                                    });
                                    if (added) {
                                        return true;
                                    }
                                }
                            }
                        }
                    } catch (e) {
                        const error = e as TransactionExecutionError;
                        const errorCause = error.cause;

                        // If user rejected the transaction on Metamask and
                        // a handler for this case has been provided
                        if (errorCause instanceof UserRejectedRequestError) {
                            // eslint-disable-next-line no-console
                            console.log('Mint error: Transaction rejected by user');
                            onRejectTransaction?.();
                        } else {
                            // eslint-disable-next-line no-console
                            console.error(`Mint error: ${JSON.stringify(error)}`);
                        }
                    }
                } else {
                    return false;
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(`Signature error: ${e}`);
                return false;
            }
        },
        [
            dispatch,
            generateSignature,
            mintContractAddress,
            onRejectTransaction,
            setMintingSuccessOptimistic,
            watchAssets,
        ],
    );
    const mintYat = useCallback(
        async ({ account, token }: MintTokenArgs) => {
            setMintingInitiated(true);
            const mintYatSuccess = await handleMint(ArtifactType.Yat, account, token);
            if (mintYatSuccess !== undefined) {
                if (mintYatSuccess) {
                    setMintingInitiated(false);
                } else if (!mintYatSuccess) {
                    setMintingFailed(true);
                }
            }
        },
        [handleMint, setMintingFailed, setMintingInitiated],
    );
    const mintArtifact = useCallback(
        async ({ type = ArtifactType.PrismCase, account, token }: MintTokenArgs) => {
            const mintArtifactSuccess = await handleMint(type, account, token);
            if (mintArtifactSuccess) {
                registerEvent(Events.artifact_minted);
            }
        },
        [handleMint],
    );
    return { mintYat, mintArtifact };
}
